import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Upload,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Loader2,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  DollarSign,
  Target,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Zap,
  Award,
  AlertCircle,
  Trash2,
  X
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

// Campaign Insights Panel Component - Updated with smart empty states
const CampaignInsightsPanel = ({ totals, campaigns, dailyTrend, dataRange, selectedPeriod, onGoToLatest }) => {
  const hasCampaignData = totals?.spend > 0;
  const hasAnyData = dataRange?.has_data;

  // Show smart empty state when no data for selected month but data exists elsewhere
  if (!hasCampaignData) {
    return (
      <div className="bg-[#1a2744] rounded-lg p-4 border border-gray-700/50 sticky top-4">
        <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-400" />
          Campaign Insights
        </h3>
        <div className="text-center py-4">
          <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          {hasAnyData ? (
            <>
              <p className="text-xs text-gray-400 mb-2">No data for {selectedPeriod}</p>
              <p className="text-[10px] text-gray-500 mb-3">
                Data available: {dataRange.earliest_month} – {dataRange.latest_month}
              </p>
              {dataRange.latest_month && (
                <button
                  onClick={() => onGoToLatest && onGoToLatest(dataRange.latest_month)}
                  className="text-[10px] text-purple-400 hover:text-purple-300 underline"
                >
                  Go to {dataRange.latest_month}
                </button>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-500">Upload a campaign report to see insights</p>
          )}
        </div>
      </div>
    );
  }

  // Calculate insights
  const spendEfficiency = totals.sales > 0 ? (totals.spend / totals.sales).toFixed(2) : 0;
  const acos = totals.acos || 0;
  const roas = totals.roas || 0;

  // ACOS Health
  const getAcosHealth = () => {
    if (acos < 25) return { color: 'text-emerald-400', bg: 'bg-emerald-500/20', dot: '🟢', label: 'Excellent', desc: 'Strong profitability on ad spend' };
    if (acos < 35) return { color: 'text-blue-400', bg: 'bg-blue-500/20', dot: '🔵', label: 'Good', desc: 'Healthy ad efficiency' };
    if (acos < 50) return { color: 'text-amber-400', bg: 'bg-amber-500/20', dot: '🟡', label: 'Warning', desc: 'Ad costs eating into margins' };
    return { color: 'text-red-400', bg: 'bg-red-500/20', dot: '🔴', label: 'Critical', desc: 'Losing money on ads' };
  };

  // ROAS Assessment
  const getRoasHealth = () => {
    if (roas >= 4) return { color: 'text-emerald-400', dot: '🟢', label: 'Strong' };
    if (roas >= 2.5) return { color: 'text-blue-400', dot: '🔵', label: 'Healthy' };
    if (roas >= 1.5) return { color: 'text-amber-400', dot: '🟡', label: 'Marginal' };
    return { color: 'text-red-400', dot: '🔴', label: 'Poor' };
  };

  const acosHealth = getAcosHealth();
  const roasHealth = getRoasHealth();

  // Top performer (lowest ACOS) and needs attention (highest ACOS > 40%)
  const sortedByAcos = [...(campaigns || [])].sort((a, b) => a.acos - b.acos);
  const topPerformer = sortedByAcos[0];
  const needsAttention = sortedByAcos.filter(c => c.acos > 40).sort((a, b) => b.acos - a.acos)[0];

  // Daily averages
  const daysWithData = dailyTrend?.length || 1;
  const avgDailySpend = totals.spend / daysWithData;
  const avgDailySales = totals.sales / daysWithData;

  return (
    <div className="bg-[#1a2744] rounded-lg p-4 border border-gray-700/50 sticky top-4">
      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-purple-400" />
        Campaign Insights
      </h3>
      
      <div className="space-y-4">
        {/* Spend Efficiency */}
        <div className="p-2.5 bg-[#0f1729] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Spend Efficiency</span>
          </div>
          <p className="text-xs text-gray-300">
            You spend <span className="font-mono text-white font-medium">${spendEfficiency}</span> to earn $1.00 in PPC revenue
          </p>
        </div>

        {/* ACOS Health */}
        <div className={`p-2.5 rounded-lg ${acosHealth.bg}`}>
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-3.5 h-3.5" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">ACOS Health</span>
          </div>
          <p className="text-xs">
            <span className={`font-mono font-medium ${acosHealth.color}`}>{acos}%</span>
            <span className="text-gray-400 ml-2">{acosHealth.dot} {acosHealth.label}</span>
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">{acosHealth.desc}</p>
        </div>

        {/* ROAS Assessment */}
        <div className="p-2.5 bg-[#0f1729] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">ROAS Assessment</span>
          </div>
          <p className="text-xs">
            <span className={roasHealth.color}>{roasHealth.dot} {roasHealth.label}</span>
            <span className="text-gray-400 ml-1">— Every $1 generates </span>
            <span className="font-mono text-white font-medium">${roas.toFixed(2)}</span>
          </p>
        </div>

        {/* Top Performer */}
        {topPerformer && (
          <div className="p-2.5 bg-[#0f1729] rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">Top Performer</span>
            </div>
            <p className="text-xs text-gray-300">
              Best campaign: <span className="text-white font-medium truncate inline-block max-w-[140px] align-bottom" title={topPerformer.campaign_name}>{topPerformer.campaign_name}</span>
            </p>
            <p className="text-[10px] text-emerald-400 mt-0.5">at {topPerformer.acos}% ACOS</p>
          </div>
        )}

        {/* Needs Attention */}
        {needsAttention && (
          <div className="p-2.5 bg-red-500/10 rounded-lg border border-red-500/20">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-3.5 h-3.5 text-red-400" />
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">Needs Attention</span>
            </div>
            <p className="text-xs text-gray-300">
              Review: <span className="text-white font-medium truncate inline-block max-w-[140px] align-bottom" title={needsAttention.campaign_name}>{needsAttention.campaign_name}</span>
            </p>
            <p className="text-[10px] text-red-400 mt-0.5">at {needsAttention.acos}% ACOS</p>
          </div>
        )}

        {/* Daily Average */}
        <div className="p-2.5 bg-[#0f1729] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Daily Average</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-400">Spend: <span className="font-mono text-white">${avgDailySpend.toFixed(2)}</span></span>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">Sales: <span className="font-mono text-emerald-400">${avgDailySales.toFixed(2)}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Smart Empty State Banner Component
const SmartEmptyBanner = ({ selectedPeriod, dataRange, onGoToLatest, section }) => {
  if (!dataRange?.has_data) {
    return (
      <div className="text-center py-8">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-600" />
        <p className="text-sm text-gray-400">No campaign data yet</p>
        <p className="text-xs text-gray-500 mt-1">Upload a campaign report to see {section}</p>
      </div>
    );
  }

  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
      <p className="text-sm text-amber-400 mb-1">
        No campaign data for {selectedPeriod}
      </p>
      <p className="text-xs text-gray-400 mb-2">
        Data available: {dataRange.earliest_month} – {dataRange.latest_month}
      </p>
      <button
        onClick={() => onGoToLatest && onGoToLatest(dataRange.latest_month)}
        className="text-xs px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded transition-colors"
      >
        Go to {dataRange.latest_month}
      </button>
    </div>
  );
};

const CampaignsTab = ({ tenantId, selectedPeriod, onUpdate, onPeriodChange }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [dataRange, setDataRange] = useState(null);
  const [expandedSection, setExpandedSection] = useState('performance');
  const [dragActive, setDragActive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch data range on mount
  useEffect(() => {
    const fetchDataRange = async () => {
      try {
        const res = await api.get('/api/v1/campaigns/data-range', {
          headers: { 'X-Tenant-ID': tenantId }
        });
        setDataRange(res.data || res);
      } catch (error) {
        console.error('Error fetching data range:', error);
      }
    };
    fetchDataRange();
  }, [tenantId]);

  // Fetch campaign overview data
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewRes, historyRes, rangeRes] = await Promise.all([
        api.get(`/api/v1/campaigns/overview?month=${selectedPeriod}`, {
          headers: { 'X-Tenant-ID': tenantId }
        }),
        api.get('/api/v1/campaigns/upload-history', {
          headers: { 'X-Tenant-ID': tenantId }
        }),
        api.get('/api/v1/campaigns/data-range', {
          headers: { 'X-Tenant-ID': tenantId }
        })
      ]);
      setOverviewData(overviewRes.data || overviewRes);
      setUploadHistory((historyRes.data || historyRes).uploads || []);
      setDataRange(rangeRes.data || rangeRes);
    } catch (error) {
      console.error('Error fetching campaign data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedPeriod]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Handle go to latest month with data
  const handleGoToLatest = (month) => {
    if (onPeriodChange) {
      onPeriodChange(month);
    }
  };

  // Handle file upload with progress
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['.csv', '.xls', '.xlsx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(ext)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    // Check file size (max 20MB)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max size is 20MB`);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress({ stage: 'reading', percent: 10 });
      
      const formData = new FormData();
      formData.append('file', file);
      
      setUploadProgress({ stage: 'uploading', percent: 30 });

      const response = await api.post('/api/v1/campaigns/upload-csv', formData, {
        headers: { 
          'X-Tenant-ID': tenantId,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 120 second timeout for large files
      });
      
      setUploadProgress({ stage: 'processing', percent: 80 });

      const result = response.data || response;
      if (result.success) {
        setUploadProgress({ stage: 'complete', percent: 100 });
        // Show inserted vs updated counts
        const { records_inserted, records_updated, campaigns } = result.summary;
        if (records_updated > 0) {
          toast.success(`Imported ${records_inserted + records_updated} records (${records_inserted} new, ${records_updated} updated) from ${campaigns.length} campaigns`);
        } else {
          toast.success(`Imported ${records_inserted} records from ${campaigns.length} campaigns`);
        }
        fetchOverview();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // Handle delete upload
  const handleDeleteUpload = async (upload) => {
    try {
      setDeleting(true);
      const response = await api.delete(`/api/v1/campaigns/upload/${upload.batch_id}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      const result = response.data || response;
      if (result.success) {
        toast.success(`Deleted ${result.deleted.metrics_records} records from "${upload.filename}"`);
        fetchOverview();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete upload');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Format helpers
  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Number(value) || 0);
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'excellent': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      case 'good': return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      case 'warning': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'critical': return 'text-red-400 bg-red-500/10 border-red-500/30';
      default: return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
    }
  };

  const getAcosColor = (acos) => {
    if (acos < 25) return 'text-emerald-400';
    if (acos < 35) return 'text-blue-400';
    if (acos < 50) return 'text-amber-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
        <span className="ml-2 text-gray-400 text-sm">Loading campaign data...</span>
      </div>
    );
  }

  const totals = overviewData?.totals || {};
  const campaigns = overviewData?.campaigns || [];
  const hasCampaignData = totals.spend > 0;

  // Calculate max values for dual Y-axis chart
  const dailyTrend = overviewData?.daily_trend || [];
  const maxSpend = Math.max(...dailyTrend.map(d => d.spend), 1);
  const maxSales = Math.max(...dailyTrend.map(d => d.sales), 1);

  return (
    <div className="flex gap-4">
      {/* Main Content - 3 Collapsible Sections */}
      <div className="flex-1 space-y-4 max-w-4xl">
        {/* Performance Metrics Section */}
        <div className="border border-gray-700/50 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'performance' ? '' : 'performance')}
            className="w-full flex items-center justify-between p-3 bg-[#1a2744] hover:bg-[#1e2d4d] transition-colors"
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-white">Campaign Performance</span>
              {hasCampaignData && (
                <span className="text-xs text-gray-400">
                  ({selectedPeriod})
                </span>
              )}
            </div>
            {expandedSection === 'performance' ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'performance' && (
            <div className="p-3 bg-[#0f1729]">
              {!hasCampaignData ? (
                <SmartEmptyBanner 
                  selectedPeriod={selectedPeriod} 
                  dataRange={dataRange} 
                  onGoToLatest={handleGoToLatest}
                  section="metrics"
                />
              ) : (
                <div className="space-y-4">
                  {/* Key Metrics Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">Spend</p>
                      <p className="font-mono text-sm text-white">{formatCurrency(totals.spend)}</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">Sales</p>
                      <p className="font-mono text-sm text-emerald-400">{formatCurrency(totals.sales)}</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">ACOS</p>
                      <p className={`font-mono text-sm ${getAcosColor(totals.acos)}`}>{totals.acos}%</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">ROAS</p>
                      <p className="font-mono text-sm text-blue-400">{totals.roas}x</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">Orders</p>
                      <p className="font-mono text-sm text-white">{formatNumber(totals.orders)}</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">Clicks</p>
                      <p className="font-mono text-sm text-white">{formatNumber(totals.clicks)}</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">CTR</p>
                      <p className="font-mono text-sm text-white">{totals.ctr}%</p>
                    </div>
                    <div className="bg-[#1a2744] rounded p-2 text-center">
                      <p className="text-[9px] text-gray-400 uppercase">CPC</p>
                      <p className="font-mono text-sm text-white">${totals.cpc}</p>
                    </div>
                  </div>

                  {/* Dual Y-Axis Daily Trend Chart */}
                  {dailyTrend.length > 0 && (
                    <div className="bg-[#1a2744] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-gray-400">Daily Spend vs Sales</p>
                        <div className="flex items-center gap-4 text-[9px] text-gray-500">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-400 rounded" />
                            Spend (left axis)
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-emerald-400 rounded" />
                            Sales (right axis)
                          </span>
                        </div>
                      </div>
                      
                      {/* Chart with dual Y-axis */}
                      <div className="relative h-40">
                        {/* Y-axis labels - Spend (left) */}
                        <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-[9px] text-red-400/70 font-mono">
                          <span>${Math.round(maxSpend)}</span>
                          <span>${Math.round(maxSpend / 2)}</span>
                          <span>$0</span>
                        </div>
                        
                        {/* Y-axis labels - Sales (right) */}
                        <div className="absolute right-0 top-0 bottom-0 w-12 flex flex-col justify-between text-[9px] text-emerald-400/70 font-mono text-right">
                          <span>${Math.round(maxSales)}</span>
                          <span>${Math.round(maxSales / 2)}</span>
                          <span>$0</span>
                        </div>
                        
                        {/* Chart area */}
                        <div className="absolute left-14 right-14 top-0 bottom-0 flex items-end gap-1 border-l border-b border-gray-700/50">
                          {dailyTrend.slice(-14).map((day, idx) => {
                            const spendHeight = (day.spend / maxSpend) * 100;
                            const salesHeight = (day.sales / maxSales) * 100;
                            return (
                              <div 
                                key={idx} 
                                className="flex gap-0.5 items-end flex-1 group relative"
                                title={`${day.date}\nSpend: $${day.spend.toFixed(2)}\nSales: $${day.sales.toFixed(2)}`}
                              >
                                <div 
                                  className="bg-red-400/80 rounded-t flex-1 transition-all hover:bg-red-400" 
                                  style={{ height: `${Math.max(spendHeight, 2)}%` }}
                                />
                                <div 
                                  className="bg-emerald-400/80 rounded-t flex-1 transition-all hover:bg-emerald-400" 
                                  style={{ height: `${Math.max(salesHeight, 2)}%` }}
                                />
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 rounded text-[9px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  <p className="text-gray-400">{day.date}</p>
                                  <p className="text-red-400">Spend: ${day.spend.toFixed(2)}</p>
                                  <p className="text-emerald-400">Sales: ${day.sales.toFixed(2)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Campaign List Section */}
        <div className="border border-gray-700/50 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'campaigns' ? '' : 'campaigns')}
            className="w-full flex items-center justify-between p-3 bg-[#1a2744] hover:bg-[#1e2d4d] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-white">Campaign List</span>
              {campaigns.length > 0 && (
                <span className="text-xs text-gray-400">
                  ({campaigns.length} campaigns)
                </span>
              )}
            </div>
            {expandedSection === 'campaigns' ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'campaigns' && (
            <div className="p-3 bg-[#0f1729]">
              {campaigns.length === 0 ? (
                <SmartEmptyBanner 
                  selectedPeriod={selectedPeriod} 
                  dataRange={dataRange} 
                  onGoToLatest={handleGoToLatest}
                  section="campaigns"
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-2 px-2 text-gray-400 font-medium">Campaign</th>
                        <th className="text-right py-2 px-2 text-gray-400 font-medium">Status</th>
                        <th className="text-right py-2 px-2 text-gray-400 font-medium">Spend</th>
                        <th className="text-right py-2 px-2 text-gray-400 font-medium">Sales</th>
                        <th className="text-right py-2 px-2 text-gray-400 font-medium">ACOS</th>
                        <th className="text-right py-2 px-2 text-gray-400 font-medium">ROAS</th>
                        <th className="text-right py-2 px-2 text-gray-400 font-medium">Orders</th>
                        <th className="text-center py-2 px-2 text-gray-400 font-medium">Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={campaign.campaign_id || idx} className="border-b border-gray-700/50 hover:bg-[#1a2744]/50">
                          <td className="py-2 px-2">
                            <div className="max-w-[200px] truncate text-white" title={campaign.campaign_name}>
                              {campaign.campaign_name}
                            </div>
                          </td>
                          <td className="text-right py-2 px-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              campaign.status === 'ENABLED' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="text-right py-2 px-2 font-mono text-white">{formatCurrency(campaign.spend)}</td>
                          <td className="text-right py-2 px-2 font-mono text-emerald-400">{formatCurrency(campaign.sales)}</td>
                          <td className={`text-right py-2 px-2 font-mono ${getAcosColor(campaign.acos)}`}>{campaign.acos}%</td>
                          <td className="text-right py-2 px-2 font-mono text-blue-400">{campaign.roas}x</td>
                          <td className="text-right py-2 px-2 font-mono text-white">{campaign.orders}</td>
                          <td className="text-center py-2 px-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getHealthColor(campaign.health)}`}>
                              {campaign.health}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="border border-gray-700/50 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'upload' ? '' : 'upload')}
            className="w-full flex items-center justify-between p-3 bg-[#1a2744] hover:bg-[#1e2d4d] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-white">Upload Campaign Report</span>
            </div>
            {expandedSection === 'upload' ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'upload' && (
            <div className="p-3 bg-[#0f1729]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Upload Zone */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive 
                      ? 'border-purple-400 bg-purple-500/10' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-400 mb-2" />
                      <p className="text-sm text-gray-300 mb-2">
                        {uploadProgress?.stage === 'reading' && 'Reading file...'}
                        {uploadProgress?.stage === 'uploading' && 'Uploading to server...'}
                        {uploadProgress?.stage === 'processing' && 'Processing records...'}
                        {uploadProgress?.stage === 'complete' && 'Complete!'}
                        {!uploadProgress?.stage && 'Uploading and processing...'}
                      </p>
                      {/* Progress bar */}
                      <div className="w-full max-w-[200px] bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-purple-500 h-full rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress?.percent || 0}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {uploadProgress?.percent || 0}% - Large files may take up to 2 minutes
                      </p>
                    </div>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-gray-500" />
                      <p className="text-sm text-gray-300 mb-1">
                        Drag & drop your Amazon campaign report
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        Supports CSV and Excel (.xls, .xlsx) up to 20MB
                      </p>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".csv,.xls,.xlsx"
                          onChange={handleFileSelect}
                          className="hidden"
                          data-testid="campaign-file-input"
                        />
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded transition-colors">
                          <Upload className="w-3 h-3" />
                          Select File
                        </span>
                      </label>
                    </>
                  )}
                </div>

                {/* Upload History with Delete buttons */}
                <div className="bg-[#1a2744] rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Recent Uploads
                  </div>
                  {uploadHistory.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-4">No uploads yet</p>
                  ) : (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {uploadHistory.slice(0, 5).map((upload, idx) => (
                        <div key={upload.batch_id || idx} className="flex items-center justify-between p-2 bg-[#0f1729] rounded text-xs group hover:bg-[#162036]">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                            <div>
                              <p className="text-white truncate max-w-[100px]" title={upload.filename}>
                                {upload.filename}
                              </p>
                              <p className="text-gray-500 text-[10px]">
                                {upload.records_count} records • {upload.campaigns_count} campaigns
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-gray-400">
                                {new Date(upload.uploaded_at).toLocaleDateString()}
                              </p>
                              <p className="text-emerald-400 text-[10px]">
                                {formatCurrency(upload.total_spend)} spent
                              </p>
                            </div>
                            {/* Delete button - always visible but subtle */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(upload); }}
                              className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                              title="Delete this upload"
                              data-testid={`delete-upload-${upload.batch_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-4 p-3 bg-[#1a2744] rounded-lg">
                <p className="text-xs text-gray-400 mb-2">How to export from Amazon Seller Central:</p>
                <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside">
                  <li>Go to Campaign Manager → Sponsored Products</li>
                  <li>Select your date range and campaigns</li>
                  <li>Click &quot;Export&quot; → &quot;Campaigns&quot;</li>
                  <li>Upload the downloaded CSV or Excel file here</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Campaign Insights Panel */}
      <div className="w-72 flex-shrink-0 hidden xl:block">
        <CampaignInsightsPanel 
          totals={totals} 
          campaigns={campaigns}
          dailyTrend={dailyTrend}
          dataRange={dataRange}
          selectedPeriod={selectedPeriod}
          onGoToLatest={handleGoToLatest}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1a2744] rounded-lg p-4 max-w-sm mx-4 border border-gray-700">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-medium text-white">Delete Upload?</h3>
              </div>
              <button 
                onClick={() => setDeleteConfirm(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-xs text-gray-300 mb-2">
              This will remove <span className="text-white font-medium">{deleteConfirm.records_count} records</span> from {deleteConfirm.campaigns_count} campaigns.
            </p>
            <p className="text-xs text-amber-400 mb-2">
              This action cannot be undone.
            </p>
            <p className="text-xs text-gray-400 mb-4">
              File: <span className="text-gray-300">{deleteConfirm.filename}</span>
            </p>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUpload(deleteConfirm)}
                disabled={deleting}
                className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors flex items-center gap-1.5"
                data-testid="confirm-delete-upload"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignsTab;
